import { api } from '@@/services//api';

export const messageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    fetchMessages: builder.query<
      { docs: Alacaza.ScheduledMessage[] },
      { propertyId?: string; status?: string; page: number; limit: number }
    >({
      query: ({ propertyId, status, page, limit }) => ({
        url: `/partners/messages`,
        params: { propertyId, status, page, limit },
      }),
      providesTags: ['Messages'],
    }),
    fetchMessage: builder.query<Alacaza.ScheduledMessage, string>({
      query: (messageId) => `/partners/messages/${messageId}`,
      providesTags: ['Message'],
    }),
    createMessage: builder.mutation<
      Alacaza.ScheduledMessage,
      {
        message?: string;
        scheduledAt: Date;
        sendToAllProperties: boolean;
        propertyId?: string;
        buildings?: string[];
        floors?: string[];
        residentTypes?: string[];
        isMemberOfCondominiumCouncil?: boolean;
        attachment?: File;
        post?: Alacaza.PostForm;
      }
    >({
      query: (params) => {
        let url = '/partners/messages';
        const body = new FormData();

        if (params.post) {
          // Publication
          url = '/partners/posts/poll';
          if (params.post.images) {
            for (const image of params.post.images) {
              body.append('file', image);
            }
          }

          body.append(
            'post',
            JSON.stringify({
              ...params.post,
              authorizeCustomOptions: !!params.post.authorizeCustomOptions,
              authorizeMultipleChoices: !!params.post.authorizeMultipleChoices,
              images: undefined,
            }),
          );
        } else {
          // Standard message
          if (params.message) {
            body.append('message', params.message);
          }

          if (params.attachment?.type) {
            body.append('file', params.attachment);
          }
        }

        // Common part
        if (params.scheduledAt) {
          body.append('scheduledAt', String(params.scheduledAt));
        }

        body.append(
          'sendToAllProperties',
          String(!!params.sendToAllProperties),
        );

        if (params.isMemberOfCondominiumCouncil) {
          body.append(
            'isMemberOfCondominiumCouncil',
            String(params.isMemberOfCondominiumCouncil),
          );
        }

        if (params.propertyId) {
          body.append('propertyIds[]', params.propertyId);
        }

        if (params.buildings?.length) {
          params.buildings.forEach((buildingId) => {
            body.append('buildingIds[]', buildingId);
          });
        }

        if (params.floors?.length) {
          params.floors.forEach((floor: string) => {
            body.append('floors[]', floor);
          });
        }

        if (params.residentTypes?.length) {
          params.residentTypes.forEach((residentType: string) => {
            body.append('residentTypes[]', residentType);
          });
        }

        return {
          url,
          method: 'POST',
          body,
          formData: true,
        };
      },
      invalidatesTags: ['Messages'],
    }),
    patchMessage: builder.mutation<
      Alacaza.ScheduledMessage,
      { id: string; message?: string; scheduledAt?: Date; attachment?: File }
    >({
      query: ({ id, message, scheduledAt, attachment }) => {
        const body = new FormData();

        if (attachment) {
          body.append('file', attachment);
        }

        if (message) {
          body.append('message', message);
        }

        if (scheduledAt) {
          body.append('scheduledAt', String(scheduledAt));
        }

        return {
          url: `/partners/messages/${id}`,
          method: 'PATCH',
          body,
          formData: true,
        };
      },
      invalidatesTags: ['Messages', 'Message'],
    }),
    deleteMessage: builder.mutation<unknown, string>({
      query: (messageId) => ({
        url: `/partners/messages/${messageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Messages'],
    }),
    deleteDocumentMessage: builder.mutation<
      unknown,
      { messageId: string; documentId: string }
    >({
      query: ({ messageId, documentId }) => ({
        url: `/partners/messages/${messageId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Messages', 'Message'],
    }),
  }),
});

export const {
  useFetchMessageQuery,
  useCreateMessageMutation,
  useFetchMessagesQuery,
  usePatchMessageMutation,
  useDeleteMessageMutation,
  useDeleteDocumentMessageMutation,
} = messageApi;
